import {
  Component,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  ChangeDetectionStrategy,
  computed,
  input,
} from '@angular/core';
import { IActiveTicket, ITrip, ITripDetail } from 'src/app/models';
import { CommunicationUserType, ExpeditionStatus, TripsViewMode, UserRole } from 'src/app/models/enums';
import { APP } from 'src/app/config/app';
import { StorageService, UtilsService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';
import {
  ACTIVE_TICKET_CATEGORY_STYLES,
  activeTicketsCategory,
  activeTicketsSummary,
  canCreateTicketFor,
} from 'src/app/utils/suggestionTickets';
import { Router } from '@angular/router';

@Component({
  selector: 'app-trip-row',
  templateUrl: './trip-row.component.html',
  styleUrl: './trip-row.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TripRowComponent {
  @Input() trip: ITrip;
  @Input() language: string;
  @Input() index: number;
  @Input() tripDetails: boolean = false;
  @Input() activeTickets: IActiveTicket[] = [];
  @Input() basePath = APP.TRIPS_BASE_PATHS[TripsViewMode.TRIPS];
  readonly followsPositions = input(false);
  readonly dimmed = input(false);
  readonly positionsDelay = input<number>();
  readonly positionsLate = computed(() => this.utilsService.isLate(this.positionsDelay()));
  readonly positionsOnTime = computed(() => this.positionsDelay() != null && !this.positionsLate());
  @Output() getTripEventEmitter = new EventEmitter<number>();
  @Output() showChangeDriverEventEmitter = new EventEmitter<number>();
  @Output() showChangeVehicleEventEmitter = new EventEmitter<number>();
  @Output() showEditCommentEventEmitter = new EventEmitter<number>();
  @Output() showViewBookingsEventEmitter = new EventEmitter<number>();
  @Output() showSetReadyEventEmitter = new EventEmitter<number>();
  @Output() showSetClosedEventEmitter = new EventEmitter<number>();
  @Output() showDeleteTripEventEmitter = new EventEmitter<number>();
  @Output() newTicketEventEmitter = new EventEmitter<ITrip>();

  public showOptions: boolean = false;
  public canChangeDriver = false;
  public canCreateTicket = false;
  public activeTicketsSummary = activeTicketsSummary;

  public TooltipPosition = TooltipPosition;
  readonly ExpeditionStatus = ExpeditionStatus;
  public images = images;

  constructor(
    renderer: Renderer2,
    public utilsService: UtilsService,
    public literalService: LiteralService,
    public router: Router,
    private storageService: StorageService,
  ) {
    // Operators cannot reassign a driver nor open driver tickets: that is for admins and driver managers
    this.storageService.getRole().then((role: UserRole) => {
      this.canChangeDriver = role !== UserRole.OPERATOR;
      this.canCreateTicket = canCreateTicketFor(role, CommunicationUserType.DRIVERS);
    });
    renderer.listen('window', 'click', (e: Event) => {
      const modal = document.getElementById('actionsModal' + this.trip.id)!;
      const close = utilsService.closeCustomModals(e, modal, modal);
      if (close) {
        this.showOptions = false;
      }
    });
  }

  moreInfo(event: any, trip: ITrip) {
    event.stopPropagation();
    this.showOptions = !this.showOptions;
    const row = document
      .getElementById('tripMoreOptions' + trip.id)
      ?.getBoundingClientRect();
    if (row) {
      document.getElementById('actionsModal' + trip.id)!.style.top =
        (row.top - 80).toString() + 'px';
      document.getElementById('actionsModal' + trip.id)!.style.left =
        (row.left + 10).toString() + 'px';
    }
  }

  hasAlteration(): boolean {
    return !!(this.trip.alterationId || (this.trip as ITripDetail).alteration);
  }

  createTicket() {
    this.showOptions = false;
    this.newTicketEventEmitter.emit(this.trip);
  }

  shouldHaveStarted(dateTime?: Date): boolean {
    return dateTime ? new Date(dateTime) < new Date() : false;
  }

  bookingsPath(tripId: number): (string | number)[] {
    return this.tripDetails ? [this.basePath, tripId, 'bookings'] : [this.basePath, 'bookings', tripId];
  }

  openBookings(event: MouseEvent, tripId: number): void {
    event.preventDefault();
    // Preserve query params (page, search, filters) so the trips list stays on
    // the current page and trip lookup in showViewBookings finds the row.
    this.router.navigate(this.bookingsPath(tripId), { queryParamsHandling: 'preserve' });
  }

  activeTicketsStyle() {
    return ACTIVE_TICKET_CATEGORY_STYLES[activeTicketsCategory(this.activeTickets)];
  }
}
